import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";
import { BsThreeDots } from "react-icons/bs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDeleteNotification } from "../../api/mutations/useDeleteNotification";
import { useSaveNotification } from "../../api/mutations/useSaveNotification";
import { useGetConnectionRequests } from "../../api/queries/useGetConnectionRequests";
import { useGetConnections } from "../../api/queries/useGetConnections";
import { useGetUserNotification } from "../../api/queries/useGetUserNotification";
import { useAppContext } from "../../Context/AppContext";
import { Notification } from "../../types/Types";
import {
  collectNotificationAvatarsByName,
  isMessageNotification,
  isNavigableSystemNotification,
  isUserOriginatedNotification,
  resolveNotificationAvatarUrl,
  resolveNotificationPath,
} from "../../utils/notificationRouting";

const LIST_MIN_HEIGHT = 240;
const LIST_STYLE: React.CSSProperties = { minHeight: LIST_MIN_HEIGHT };
const NOTIFICATION_AVATAR_SIZE = 48;

function NotificationsInner() {
  const { t } = useTranslation();
  const { user } = useAppContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDropdown, setShowDropdown] = useState<number | null>(null);
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);
  const [showModal, setShowModal] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const openedFromQueryRef = useRef<number | null>(null);

  const userId = user?.user?.id ?? 0;
  const objectId = user?.user?.objectId ?? "";

  const { data: notifications, refetch } = useGetUserNotification({
    id: userId,
    enabled: userId > 0,
    pageSize: 50,
  });

  const { data: connectionRequests } = useGetConnectionRequests({
    userId: objectId,
    enabled: Boolean(objectId),
  });

  const { data: connections } = useGetConnections({
    userId: objectId,
    enabled: Boolean(objectId),
  });

  const avatarByName = useMemo(
    () => collectNotificationAvatarsByName(connectionRequests, connections),
    [connectionRequests, connections]
  );

  const sortedNotifications = useMemo(
    () =>
      [...(notifications?.data ?? [])]
        .filter(
          (notification: { name?: string }) =>
            !isMessageNotification(notification)
        )
        .sort(
          (a: Notification, b: Notification) =>
            new Date(b.date).getTime() - new Date(a.date).getTime()
        ),
    [notifications?.data]
  );

  const { mutate: updateNotification } = useSaveNotification({
    onSuccess: () => refetch(),
  });

  const { mutate: deleteNotification } = useDeleteNotification({
    onSuccess: () => {
      refetch();
      setShowConfirmModal(false);
      setConfirmDeleteId(null);
    },
  });

  const handleConfirmDelete = useCallback(() => {
    if (confirmDeleteId !== null) {
      deleteNotification(confirmDeleteId);
    }
  }, [confirmDeleteId, deleteNotification]);

  const toggleDropdown = useCallback((id: number) => {
    setShowDropdown((prev) => (prev === id ? null : id));
  }, []);

  const handleMarkAsRead = useCallback(
    (notification: Notification) => {
      updateNotification({ ...notification, isRead: true });
      setShowDropdown(null);
    },
    [updateNotification]
  );

  const handleMarkAsUnread = useCallback(
    (notification: Notification) => {
      updateNotification({ ...notification, isRead: false });
      setShowDropdown(null);
    },
    [updateNotification]
  );

  const handleNotificationClick = useCallback(
    (notification: Notification) => {
      if (!notification.isRead) {
        updateNotification({ ...notification, isRead: true });
      }

      if (isNavigableSystemNotification(notification)) {
        navigate(resolveNotificationPath(notification));
        return;
      }

      setSelectedNotification(notification);
      setShowModal(true);
    },
    [navigate, updateNotification]
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setSelectedNotification(null);
    openedFromQueryRef.current = null;
    if (searchParams.has("id")) {
      const next = new URLSearchParams(searchParams);
      next.delete("id");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleDeleteClick = useCallback((id: number) => {
    setConfirmDeleteId(id);
    setShowConfirmModal(true);
    setShowDropdown(null);
  }, []);

  const handleCloseConfirmModal = useCallback(() => {
    setShowConfirmModal(false);
  }, []);

  useEffect(() => {
    const rawId = searchParams.get("id");
    if (!rawId || sortedNotifications.length === 0) return;

    const notificationId = Number(rawId);
    if (!Number.isFinite(notificationId)) return;
    if (openedFromQueryRef.current === notificationId) return;

    const match = sortedNotifications.find(
      (item: Notification) => item.id === notificationId
    );
    if (!match) return;

    openedFromQueryRef.current = notificationId;

    if (isNavigableSystemNotification(match)) {
      if (!match.isRead) {
        updateNotification({ ...match, isRead: true });
      }
      navigate(resolveNotificationPath(match), { replace: true });
      return;
    }

    setSelectedNotification(match);
    setShowModal(true);
    if (!match.isRead) {
      updateNotification({ ...match, isRead: true });
    }
  }, [navigate, searchParams, sortedNotifications, updateNotification]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="position-relative w-100 min-vw-0 overflow-x-hidden">
      <div className="container py-3">
        <h3 className="mb-4">{t("notifications.title")}</h3>
        <div className="list-group" style={LIST_STYLE}>
          {sortedNotifications.length === 0 ? (
            <div className="text-center text-muted py-5">
              <h5>{t("notifications.empty")}</h5>
            </div>
          ) : (
            sortedNotifications.map((item: Notification) => (
              <div
                key={item.id}
                className={`list-group-item list-group-item-action position-relative d-flex align-items-start gap-3 pe-5 ${
                  item.isRead ? "" : "bg-primary-subtle"
                }`}
                role="button"
                tabIndex={0}
                onClick={() => handleNotificationClick(item)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleNotificationClick(item);
                  }
                }}
              >
                <div
                  className="position-absolute top-0 end-0 mt-2 me-2"
                  style={{ zIndex: 2 }}
                  ref={showDropdown === item.id ? dropdownRef : null}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    variant="light"
                    size="sm"
                    className="border-0 p-0 rounded-circle d-inline-flex align-items-center justify-content-center"
                    style={{ width: 28, height: 28 }}
                    aria-label={t("notifications.menu")}
                    onClick={() => toggleDropdown(item.id)}
                  >
                    <BsThreeDots className="text-muted" />
                  </Button>
                  {showDropdown === item.id ? (
                    <div className="dropdown-menu dropdown-menu-end show position-absolute end-0 mt-1 shadow">
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => handleMarkAsRead(item)}
                      >
                        {t("notifications.markRead")}
                      </button>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => handleMarkAsUnread(item)}
                      >
                        {t("notifications.markUnread")}
                      </button>
                      <button
                        type="button"
                        className="dropdown-item text-danger"
                        onClick={() => handleDeleteClick(item.id)}
                      >
                        {t("notifications.remove")}
                      </button>
                    </div>
                  ) : null}
                </div>

                <img
                  src={resolveNotificationAvatarUrl(item, avatarByName)}
                  alt={
                    item.name
                      ? t("notifications.altFrom", { name: item.name })
                      : t("notifications.alt")
                  }
                  width={NOTIFICATION_AVATAR_SIZE}
                  height={NOTIFICATION_AVATAR_SIZE}
                  className={`rounded-circle flex-shrink-0 mt-1 ${
                    isUserOriginatedNotification(item)
                      ? "object-fit-cover"
                      : "object-fit-contain bg-light p-1"
                  }`}
                  loading="lazy"
                  decoding="async"
                />

                <div className="d-flex flex-column flex-grow-1 min-w-0 gap-1">
                  <div className="fw-bold text-break pe-1">{item.name}</div>
                  <div className="text-muted small text-break">
                    {item.description}
                  </div>
                  <div className="d-flex align-items-center justify-content-end w-100 mt-2 pb-2 border-bottom">
                    <div className="text-muted text-nowrap small">
                      {new Date(item.date).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t("notifications.modalTitle")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h5>{selectedNotification?.name}</h5>
            <p>{selectedNotification?.description}</p>
            <div className="text-muted small">
              {selectedNotification?.date &&
                new Date(selectedNotification.date).toLocaleString()}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              {t("notifications.close")}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showConfirmModal}
          onHide={handleCloseConfirmModal}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("notifications.deleteTitle")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete this notification?
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseConfirmModal}>
              {t("notifications.cancel")}
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete}>
              {t("notifications.delete")}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}

NotificationsInner.displayName = "NotificationsPage";

export default memo(NotificationsInner);
